import React, { useState, useEffect } from "react";
import userlogo from "../Assests/Applicant.jpg";
import { Drawer, Collapse, Button, message, Card, Input, Modal } from "antd";
import useAuth from "../hooks/useAuth";
import axios from "axios";
import TATTimeline from "./TATTimeline";

const { Panel } = Collapse;
const URL = process.env.REACT_APP_API_URL;

const CandidateProfileDrawer = ({ open, onClose, candidateId, onUpdateStatus, onUpdate }) => {
  const [candidateData, setCandidateData] = useState({});
  const [loading, setLoading] = useState(true);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const { token, auth, role } = useAuth();
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");
  const isCampusDrive = candidateData.isCampusDrive;
  const tatHistory = candidateData.tatHistory || [];
  const [tatDrawerOpen, setTatDrawerOpen] = useState(false);
  const [backoutModalVisible, setBackoutModalVisible] = useState(false);
  const [backoutLevel, setBackoutLevel] = useState(""); // Dropdown value
  const [backoutNote, setBackoutNote] = useState(""); // Note value
  const aniTests = candidateData.aniTests;

  //capitalize and format...
  const capitalizeAndFormat = (text) => {
    const capitalizedWord = text.charAt(0).toUpperCase() + text.slice(1).split('_').join(' ');
    return capitalizedWord;
  }


  // Function to handle the submission of the Candidate Backout modal
  const handleBackoutSubmit = async () => {
    if (!backoutLevel || !backoutNote) {
      message.warning("Please select a level and provide a note.");
      return;
    }

    const currentDate = new Date().toISOString();
    const updatedBy = auth.fullName;
    const updatedStatus = `Candidate Backout at ${backoutLevel}`;

    try {
      const updatedTatHistory = tatHistory.map((tat) => {
        if (!tat.endDate) {
          return {
            ...tat,
            tatStatus: updatedStatus,
            endDate: currentDate,
          };
        }
        return tat;
      });

      const response = await axios.put(
        `${URL}/api/candidate/${candidateId}`,
        {
          status: updatedStatus,
          tatHistory: updatedTatHistory,
          history: [
            ...(candidateData.history || []),

            {
              status: updatedStatus,
              note: `Level: ${backoutLevel}, Note: ${backoutNote}`,
              updatedBy,
              updatedAt: currentDate,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        message.success(`Candidate status updated to '${updatedStatus}'`);
        setBackoutModalVisible(false);
        setBackoutLevel("");
        setBackoutNote("");

        const updatedCandidate = {
          ...candidateData,
          status: updatedStatus,
          tatHistory: updatedTatHistory,
          history: [
            ...(candidateData.history || []),
            {
              status: updatedStatus,
              note: `Level: ${backoutLevel}, Note: ${backoutNote}`,
              updatedBy,
              updatedAt: currentDate,
            },
          ],
        };

        setCandidateData(updatedCandidate);

        // Notify CandidateCard of the update
        if (onUpdate) {
          onUpdate(updatedCandidate);
        }
      }
    } catch (error) {
      console.error("Error updating candidate status:", error);
      message.error("Error updating candidate status.");
    }
  };

  // Render the Candidate Backout modal
  const renderBackoutModal = () => (
    <Modal
      title="Candidate Backout"
      open={backoutModalVisible}
      onOk={handleBackoutSubmit}
      onCancel={() => setBackoutModalVisible(false)}
      okText="Submit"
      okButtonProps={{ disabled: !backoutLevel || !backoutNote }}
    >
      <p>Please select a level and provide a note:</p>
      <div style={{ marginBottom: "10px" }}>
        <label style={{ fontWeight: "bold" }}>Select Level:</label>
        <select
          value={backoutLevel}
          onChange={(e) => setBackoutLevel(e.target.value)}
          style={{ width: "100%", padding: "8px", marginTop: "5px" }}
        >
          <option value="">-- Select Level --</option>
          <option value="L1">Candidate Backout at L1</option>
          <option value="L2">Candidate Backout at L2</option>
          <option value="L3">Candidate Backout at L3</option>
          <option value="L4">Candidate Backout at L4</option>
        </select>
      </div>
      <div>
        <label style={{ fontWeight: "bold" }}>Note:</label>
        <Input.TextArea
          rows={4}
          value={backoutNote}
          onChange={(e) => setBackoutNote(e.target.value)}
          placeholder="Enter a note"
          required
        />
      </div>
    </Modal>
  );

  // Update the "Candidate Backout" button to open the modal


  // Add the modal rendering in the return statement
  const calculateDurationInDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(); // Use current date if endDate is missing
    const durationInMilliseconds = end - start;
    return Math.max(0, Math.ceil(durationInMilliseconds / (1000 * 60 * 60 * 24))); // Ensure no negative values
  };

  const handleTatDrawerOpen = () => {
    setTatDrawerOpen(true);
  };

  const handleTatDrawerClose = () => {
    setTatDrawerOpen(false);
  };

  const lastTat = tatHistory[tatHistory.length - 1]; // Get the last TAT entry


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${URL}/api/candidate/profile/${candidateId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();

        if (data.status === "SUCCESS") {
          // Filter only the last added L1, L2, and HR rounds
          const filteredRounds = data.data.round.reduce((acc, round) => {
            if (
              ["L1", "L2", "HR", "L3", "L4"].includes(round.roundName) &&
              (!acc[round.roundName] ||
                new Date(round.interviewDate) >
                new Date(acc[round.roundName].interviewDate))
            ) {
              acc[round.roundName] = round;
            }
            return acc;
          }, {});

          setCandidateData({
            ...data.data,
            round: Object.values(filteredRounds),
          });
        } else {
          console.error("Failed to fetch candidate data:", data.message);
        }
      } catch (error) {
        console.error("Error fetching candidate data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open && candidateId) {
      fetchData();
    }
  }, [open, candidateId, token]);

  const drawerTitle = candidateData.fullName
    ? `${candidateData.fullName}'s Profile`
    : "Candidate Profile";

  const labelStyle = { fontWeight: "bold" };
  const valueStyle = { marginLeft: "30px", textTransform: "capitalize" };
  const assessmentvalueStyle = { marginLeft: "20px", textTransform: "capitalize", float: "right" };



  const getPanelBackgroundColor = (roundName) => {
    switch (roundName) {
      case "L1":
        return "#00a2bd";
      case "L2":
        return "#00b4d2";
      case "L3":
        return "#00b4ds";
      case "L4":
        return "#00b4g2";
      case "HR":
        return "#4ccadf";
      default:
        return "#7fd9e8";
    }
  }

  const panelHeaderStyle = {
    color: "white",
    textTransform: "capitalize",
  };

  const handleHistoryDrawerOpen = () => {
    setHistoryDrawerOpen(true);
  };

  const handleHistoryDrawerClose = () => {
    setHistoryDrawerOpen(false);
  };

  const renderResumeLink = (candidate) => {
    if (candidate.resume) {
      const downloadLink = `${URL}${candidate.resume}`;
      return (
        <a href={downloadLink} target="_blank" rel="noopener noreferrer" className='resume-link'>
          {candidate.firstName} CV
        </a>
      );
    } else {
      return "Resume not available";
    }
  };


  const updateScore = async (subject) => {
    try {
      const response = await axios.put(
        `${URL}/api/candidate/${candidateId}/reset-scores`,
        { subject },
        // Only send the subject name
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        message.success(`Score reset for ${subject}`);
        setCandidateData((prevData) => ({
          ...prevData,
          [subject]: { ...prevData[subject], score: -1, status: "pending" },
        }));

      }
    } catch (error) {
      console.error(`Error resetting score for ${subject}:`, error);
      // message.error(`Error resetting score for ${subject}`);
    }
  };

  const renderScores = () => {
    if (candidateData.selectedCategory === "No-Test") {
      return <center><h1> No-Test</h1></center>;
    }

    if (!candidateData?.assessmentDone) {
      return <p>No assessment data available.</p>;
    }

    const renderScoreRow = (subject, subjectData) => (
      <span key={subject} style={labelStyle}>
        {subject}:{" "}
        {/* <span style={{ color: subjectData?.status === "pass" ? "#00B4D2" : "black" }}> */}
        <span style={assessmentvalueStyle}>
          {subjectData?.score === -1 ? 0 : subjectData?.score || 'N/A'}, {subjectData?.status ?? " "}
        </span><br />
        {/* {auth.role === "Admin" && (
          <Button
            type="link"
            onClick={() => updateScore(subject)}
            disabled={subjectData?.score === -1}
          >
            Reset
          </Button>
        )} */}
      </span>
    );

    const atsCleared = candidateData.atsCleared;
    const scores = isCampusDrive ? (
      <>
        {renderScoreRow("Psychometric", candidateData.psychometric)}
        {renderScoreRow("Vocabulary", candidateData.vocabulary)}
        {renderScoreRow("Quantitative", candidateData.quantitative)}
      </>
    ) : candidateData.selectedCategory === "ANI-Test" ? (
      <>
        {
          aniTests.map(testName => {
            return renderScoreRow(capitalizeAndFormat(testName), candidateData[testName])
          })
        }
      </>
    ) : candidateData.selectedCategory === "Technical" ? (
      <>
        {renderScoreRow("Psychometric", candidateData.psychometric)}
        {renderScoreRow("Java", candidateData.java)}
        {renderScoreRow("Vocabulary", candidateData.vocabulary)}
        {renderScoreRow("Quantitative", candidateData.quantitative)}
      </>
    ) : (
      <>
        {renderScoreRow("Vocabulary", candidateData.vocabulary)}
        {renderScoreRow("Excel", candidateData.excel)}
        {renderScoreRow("Accounts", candidateData.accounts)}
        {renderScoreRow("Quantitative", candidateData.quantitative)}
      </>
    );

    return (
      <>
        {scores}
        <div style={{ marginTop: "20px" }}>
          {!candidateData.atsCleared && (
            <>
              {/* <h4><strong style={{color: 'black', fontWeight: 'bold'}}>Test:</strong></h4> */}
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                <Button type="primary" onClick={() => handleTestStatusUpdate("Test Rejected")} style={{ background: "red", width: "30%", marginRight: "5%" }}>Rejected</Button>
                <Button type="primary" onClick={() => handleTestStatusUpdate("Test Shortlisted")} style={{ background: "#00B4D2", width: "30%", marginRight: "5%" }}>Shortlisted</Button>
              </div>
              {/* <Button type="primary" onClick={() => handleTestStatusUpdate("Re-Test")} style={{ background: "#007d93", width: "30%" }}>Re-Test</Button> */}
            </>
          )}
        </div>
      </>
    );
  };



  const handleTestStatusUpdate = async (status) => {
    if (status === "Re-Test") {
      // Reset scores of "fail" subjects
      const subjectsToReset = ["psychometric", "java", "vocabulary", "quantitative", "excel", "accounts"];
      const resetPromises = subjectsToReset.map(subject => {
        if (candidateData[subject]?.status === "Fail") {
          return updateScore(subject);
        }
        return Promise.resolve();
      });
      await Promise.all(resetPromises);
    }

    const updatedStatus = status === "Test Shortlisted" ? "L1 To be Scheduled" : status;

    try {
      const response = await axios.put(
        `${URL}/api/candidate/${candidateId}`,
        {
          atsCleared: true,
          testStatus: status,
          status: updatedStatus,
          assessmentDone: status === "Re-Test" ? false : candidateData.assessmentDone,
          history: [
            ...(candidateData.history || []),
            {
              note: `Test status updated to ${status}`,
              updatedBy: auth.fullName,
              updatedAt: new Date().toISOString(),
            },
          ]
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        message.success(`Test status updated to '${status}'`);
        setCandidateData((prevData) => ({
          ...prevData,
          testStatus: status,
          status: status,
          assessmentDone: status === "Re-Test" ? false : prevData.assessmentDone,
          history: [
            ...(prevData.history || []),
            { status: status, note: `Test status updated to ${status}`, updatedBy: auth.fullName, updatedAt: new Date().toISOString() },
          ],
        }));
      } else {
        message.error("Failed to update test status.");
      }
    } catch (error) {
      console.error("Error updating test status:", error);
      message.error("Error updating test status.");
    }
  };

  const handleRejection = async () => {
    if (!rejectionNote) {
      message.warning("Please provide a note for the rejection.");
      return;
    }

    const updatedBy = auth.fullName; // Replace with actual user role if dynamic

    try {
      await axios.put(
        `${URL}/api/candidate/${candidateId}`,
        {
          status: "CV Rejected",
          note: rejectionNote,
          history: [
            ...(candidateData.history || []), {
              status: "CV Rejected",
              note: `${rejectionNote}`,
              updatedBy: updatedBy,
              updatedAt: new Date(),
            }
          ]
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success("Candidate status updated to 'Rejected'");
      setRejectionModalVisible(false);
      setRejectionNote("");
      setCandidateData((prevData) => ({
        ...prevData,
        status: "CV Rejected",
        history: [
          ...(prevData.history || []),
          { status: "CV Rejected", note: `Rejection reason: ${rejectionNote}`, updatedBy }
        ]
      }));
    } catch (error) {
      console.error("Error rejecting candidate:", error);
      message.error("Error updating candidate status.");
    }
  };


  const renderRejectionModal = () => (
    <Modal
      title="Reject Candidate"
      open={rejectionModalVisible}
      onOk={handleRejection}
      onCancel={() => setRejectionModalVisible(false)}
      okText="Confirm Rejection"
      okButtonProps={{ disabled: !rejectionNote }}
    >
      <p>Please provide a reason for rejection:</p>
      <Input.TextArea
        rows={4}
        value={rejectionNote}
        onChange={(e) => setRejectionNote(e.target.value)}
        placeholder="Enter rejection reason"
        required
      />

    </Modal>
  );

  const handleSendEmailAndUpdateStatus = async (status) => {
    if (status === "CV Shortlisted") {
         const emailData = {
        candidates: [
            {
                "Candidate Name": candidateData.fullName,
                "Email ID": candidateData.email,
                "Role/Designation": candidateData.role,
                "HR Name": candidateData.hrName || auth.fullName,
                "password": candidateData.confirmPassword,
            }
        ]
    };
        try {
            // Send email credentials
            const emailResponse = await axios.post(`${URL}/api/user/credentials`, emailData);
            if (emailResponse.status === 201) {
                message.success("Email sent successfully!");
            } else {
                message.error("Failed to send email.");
            }
        } catch (error) {
            console.error("Error sending email:", error);
            message.error("Error sending email.");
        }
    }

    try {
      const currentDate = new Date().toISOString();
      let updatedTatHistory = [...candidateData.tatHistory];

      // Skip updating tatHistory for "CV Feedback Awaited"
      if (status !== "CV Feedback Awaited") {
        updatedTatHistory = updatedTatHistory.map((tat) => {
          if (tat.tatType === "OPS TAT" && !tat.endDate) {
            return {
              ...tat,
              tatStatus: status,
              endDate: currentDate,
            };
          }
          return tat;
        });

        if (status !== "CV Rejected" && status !== "Candidate Backout") {
          updatedTatHistory.push({
            tatType: "HR TAT",
            tatStatus: status,
            startDate: currentDate,
          });
        }
      }

      const updateResponse = await axios.put(
        `${URL}/api/candidate/${candidateId}`,
        {
          status,
          tatHistory: status === "CV Feedback Awaited" ? candidateData.tatHistory : updatedTatHistory,
          history: [
            ...(candidateData.history || []), {
              status: status,
              note: `Status updated to ${status}`,
              updatedBy: auth.fullName,
              updatedAt: currentDate,
            },
          ],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (updateResponse.status === 200) {
        message.success(`Candidate status updated to '${status}'`);
        setCandidateData((prevData) => ({
          ...prevData,
          status,
          tatHistory: status === "CV Feedback Awaited" ? prevData.tatHistory : updatedTatHistory,
          history: [
            ...(prevData.history || []),
            {
              status,
              note: `Status updated to ${status}`,
              updatedBy: auth.fullName,
              updatedAt: currentDate,
            },
          ],
        }));
        onUpdateStatus(candidateId, status);
      } else {
        message.error("Failed to update candidate status.");
      }
    } catch (error) {
      console.error("Error updating candidate status:", error);
      message.error("Error updating candidate status.");
    }
  };


  return (
    <>
      <Drawer
        title={
          <div
            style={{
              backgroundColor: "#004854",
              color: "white",
              padding: "10px",
              textTransform: "capitalize",
            }}
          >
            {drawerTitle}
          </div>
        }
        placement="right"
        closable={false}
        onClose={onClose}
        open={open}
        width={400}
      >
        <div className="profile-header">
          <img
            src={userlogo}
            alt="User Logo"
            className="user-logo"
            width={100}
          />
          <h1>{candidateData.position}</h1>

          <div style={{ textTransform: 'capitalize' }}>
            {renderResumeLink(candidateData)}
          </div>
          <br />
        </div>

        <Collapse defaultActiveKey={["1"]} accordion>
          <Panel
            header={<span style={panelHeaderStyle}>About</span>}
            key="1"
            style={{ backgroundColor: "#005a69" }}
          >
            <p>
              <span style={labelStyle}>First Name</span>
              <span style={valueStyle}>: {candidateData.firstName}</span>
            </p>
            <p>
              <span style={labelStyle}>Last Name</span>
              <span style={valueStyle}>: {candidateData.lastName}</span>
            </p>
            <p>
              <span style={labelStyle}>Qualification</span>
              <span style={valueStyle}>: {candidateData.qualification}</span>
            </p>
            <p>
              <span style={labelStyle}>Experience</span>
              <span style={valueStyle}>: {candidateData.totalExperience}</span>
            </p>
          </Panel>
          <Panel
            header={<span style={panelHeaderStyle}>Contact</span>}
            key="2"
            style={{ backgroundColor: "#007d93" }}
          >
            <p>
              <span style={labelStyle}>Email</span>
              <span>: {candidateData.email}</span>
            </p>
            <p>
              <span style={labelStyle}>Contact</span>
              <span style={valueStyle}>: {candidateData.contact}</span>
            </p>
            <p>
              <span style={labelStyle}>Address</span>
              <span style={valueStyle}>
                : {candidateData.currentLocation}, {candidateData.state}
              </span>
            </p>
          </Panel>
          {candidateData.round &&
            candidateData.round.length > 0 &&
            candidateData.round.map((round, index) => (
              <Panel
                header={`${round ? round.roundName : "Unknown"}-Round`}
                key={index}
                style={{
                  backgroundColor: round
                    ? getPanelBackgroundColor(round.roundName)
                    : "defaultColor",
                  textTransform: "capitalize",
                }}
              >
                <p>
                  <span style={labelStyle}>Round Name</span>
                  <span style={valueStyle}>
                    : {round ? round.roundName : ""}
                  </span>
                </p>
                <p>
                  <span style={labelStyle}>Panelist Name</span>
                  <span style={valueStyle}>
                    : {round ? round.panelistName : ""}
                  </span>
                </p>
                <p>
                  <span style={labelStyle}>Interview Date</span>
                  <span style={valueStyle}>
                    :{" "}
                    {new Date(
                      round ? round.interviewDate : ""
                    ).toLocaleDateString()}
                  </span>
                </p>
                <p>
                  <span style={labelStyle}>Feedback Provided</span>
                  <span style={valueStyle}>
                    : {round && round.feedbackProvided ? "Yes" : "No"}
                  </span>
                </p>
                <ul>
                  {round?.skills &&
                    round.skills.map((skill, skillIndex) => (
                      <li key={skillIndex}>
                        <strong>{skill.name}</strong>: {skill.rating} -{" "}
                        {skill.comments}
                      </li>
                    ))}
                </ul>
              </Panel>
            ))}
        </Collapse>
        <Collapse>
          <Panel
            header={<span>Others</span>}
            key="4"
            style={{
              backgroundColor: "#ccf0f6",
              color: "#000",
              textTransform: "capitalize",
            }}
          >
            <div>

              <p>
                <span style={labelStyle}>Status</span>
                <span style={valueStyle}>: {candidateData.status}</span>
              </p>
               <p>
                <span style={labelStyle}>Source</span>
                  <span style={valueStyle}>: {["company website", "job portal", "word of mouth", "social media"].includes((candidateData.source || "").toLowerCase()) ? `${candidateData.source} - Careers` : candidateData.source}</span>
              </p>
              <p>
                <span style={labelStyle}>HR</span>
                  <span style={valueStyle}>: {candidateData.mgrName === "careers" ? "To be Assigned" : candidateData.mgrName}</span>
              </p>
              <p>
                <span style={labelStyle}>Reference</span>
                <span style={valueStyle}>: {candidateData.reference}</span>
              </p>
            </div>
          </Panel>
        </Collapse>
        <Collapse accordion>
          <Panel header="Assessment Details" key="1">
            {candidateData ? (
              <p>
                {renderScores()}
              </p>
            ) : (
              <p>Loading candidate details...</p>
            )}
          </Panel>

        </Collapse>

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {candidateData.status && (candidateData.status === "Candidate Backout" || candidateData.status === "CV Rejected") ? (
            <>
              <Button
                type="primary"
                onClick={handleHistoryDrawerOpen}
                style={{ marginTop: "20px", background: "#00B4D2", alignSelf: "center" }}
              >
                View History
              </Button>
              <div>

                <h6>{candidateData.status}</h6>
              </div>
            </>
          ) : (
            <>
              {(candidateData.status &&
                (candidateData.status === "CV Sourced" ||
                  candidateData.status === "CV Feedback Awaited" ||
                  candidateData.status === "CV on Hold")) && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginTop: "20px" }}>
                      <Button
                        type="primary"
                        onClick={() => handleSendEmailAndUpdateStatus("CV Shortlisted")}
                        style={{ background: "#00B4D2", width: "48%" }}
                      >
                        CV Shortlisted
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => setRejectionModalVisible(true)}
                        style={{ background: "red", width: "48%" }}
                      >
                        CV Rejected
                      </Button>
                    </div>
                  </div>
                )}
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Button
                  type="primary"
                  onClick={() => handleSendEmailAndUpdateStatus("CV on Hold")}
                  style={{ marginTop: "20px", background: "#007d93", width: "48%" }}
                >
                  CV on Hold
                </Button>
                <Button
                  type="danger"
                  onClick={() => setBackoutModalVisible(true)}
                  style={{ marginTop: "20px", background: "red" }}
                >
                  Candidate Backout
                </Button>

              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: candidateData.status === "CV Sourced" ? "space-between" : "center",
                  width: "100%",
                }}
              >
                {candidateData.status === "CV Sourced" && (
                  <Button
                    type="primary"
                    onClick={() => handleSendEmailAndUpdateStatus("CV Feedback Awaited")}
                    style={{ background: "#00B4D2", width: "48%", marginTop: "20px" }}
                  >
                    CV Feedback Awaited
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={handleHistoryDrawerOpen}
                  style={{
                    marginTop: "20px",
                    background: "#00B4D2",
                    alignSelf: "center",
                  }}
                >
                  View History
                </Button>
              </div>

              <div>
                {/* {lastTat && (
        <Button
  type="primary"
  onClick={handleTatDrawerOpen}
  style={{ marginTop: "20px", background: "#00B4D2", alignSelf: "center" }}
>
  {`${lastTat.tatType} - ${calculateDurationInDays(lastTat.startDate, lastTat.endDate)} days`}
</Button>
)} */}
                {isCampusDrive && <h5>Campus Drive</h5>}
                <h6>{candidateData.status}</h6>
              </div>
            </>
          )}
        </div>
      </Drawer>
      {renderRejectionModal()}
      {renderBackoutModal()}
      <Drawer
        title="Candidate History"
        placement="left"
        closable={true}
        onClose={handleHistoryDrawerClose}
        open={historyDrawerOpen}
        width={400}
      >
        {candidateData.history && candidateData.history.length > 0 ? (
          candidateData.history.map((historyItem, index) => (
            <div key={index} style={{ marginBottom: "10px" }}>
              <p style={{ color: "#00B4D2" }}>
                <span style={labelStyle}>Status</span>
                <span style={valueStyle}>: {historyItem.status}</span>
              </p>
              <p>
                <span style={labelStyle}>Updated By</span>
                <span style={valueStyle}>: {historyItem.updatedBy}</span>
              </p>
              <p>
                <span style={labelStyle}>Updated At</span>
                <span style={valueStyle}>
                  : {new Date(historyItem.updatedAt).toLocaleDateString()}
                </span>
              </p>
              <p>
                <span style={labelStyle}>Note</span>
                <span style={valueStyle}>: {historyItem.note}</span>
              </p>
              <hr />
            </div>
          ))
        ) : (
          <p>No history available for this candidate.</p>
        )}
      </Drawer>
      <Drawer
        title="TAT History"
        placement="top"
        closable={true}
        onClose={handleTatDrawerClose}
        open={tatDrawerOpen}
        height={400}
      >
        {tatHistory && tatHistory.length > 0 ? (
          <TATTimeline tatHistory={tatHistory} />
        ) : (
          <p>No TAT history available.</p>
        )}
      </Drawer>
    </>
  );
};

export default CandidateProfileDrawer;
